import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DataSourceGet } from '../../models/datasource';
import { DataService } from '../../services/data.service';

@Component({
  selector: 'app-datasource-info-panel',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatChipsModule,
    MatTableModule,
    MatProgressSpinnerModule,
    MatTooltipModule
  ],
  templateUrl: './datasource-info-panel.component.html',
  styleUrls: ['./datasource-info-panel.component.scss']
})
export class DatasourceInfoPanelComponent implements OnInit, OnChanges {
  @Input() dataSource: DataSourceGet | null = null;
  @Input() uploadedFile: { name: string; data: any } | null = null;
  @Input() columnsWithTypes: Array<{name: string, type: string}> = [];
  @Output() fileUpdate = new EventEmitter<File>();
  @Output() subtitleChange = new EventEmitter<string>();

  private parsedSchema: any = null;
  
  // Data table properties
  dataRows: any[] = [];
  displayedColumns: string[] = [];
  loadingData = false;
  dataLoadError: string | null = null;

  constructor(private dataService: DataService) {}

  ngOnInit(): void {
    this.loadDataIfNeeded();
    this.emitSubtitle();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dataSource'] && !changes['dataSource'].firstChange) {
      this.loadDataIfNeeded();
      this.emitSubtitle();
    }
    if ((changes['uploadedFile'] && !changes['uploadedFile'].firstChange) ||
        (changes['columnsWithTypes'] && !changes['columnsWithTypes'].firstChange)) {
      this.emitSubtitle();
    }
  }

  private emitSubtitle(): void {
    if (this.dataSource && this.dataSource.dataSourceTypeId === 3) {
      this.subtitleChange.emit('Data Preview');
    } else if (this.dataSource && this.dataSource.dataSourceTypeId === 1) {
      this.subtitleChange.emit(this.hasSchema ? 'API Schema' : 'API Listener');
    } else if (this.columns.length > 0) {
      this.subtitleChange.emit('Available Columns');
    } else {
      this.subtitleChange.emit('');
    }
  }

  private loadDataIfNeeded(): void {
    // Only load data if datasource is of Upload File type (dataSourceTypeId === 3)
    if (this.dataSource && this.dataSource.dataSourceTypeId === 3 && this.dataSource.dataSourceId) {
      this.loadingData = true;
      this.dataLoadError = null;
      
      this.dataService.getDataSourceData(this.dataSource.dataSourceId).subscribe({
        next: (response: any) => {
          this.processDataResponse(response);
          this.loadingData = false;
        },
        error: (error) => {
          console.error('Failed to load datasource data:', error);
          this.dataLoadError = 'Failed to load data';
          this.loadingData = false;
        }
      });
    } else {
      // Clear data if not an upload file type
      this.dataRows = [];
      this.displayedColumns = [];
    }
  }

  private processDataResponse(response: any): void {
    try {
      // The response might be a string (JSON) or already parsed
      let data = response;
      
      if (typeof response === 'string') {
        data = JSON.parse(response);
      }
      
      // Handle different response structures
      if (Array.isArray(data)) {
        this.dataRows = data.slice(0, 100); // Limit to first 100 rows for display
      } else if (data.data && Array.isArray(data.data)) {
        this.dataRows = data.data.slice(0, 100);
      } else {
        console.warn('Unexpected data format:', data);
        this.dataRows = [];
      }
      
      // Extract column names from first row if data exists
      if (this.dataRows.length > 0) {
        this.displayedColumns = Object.keys(this.dataRows[0]);
      }
    } catch (error) {
      console.error('Error processing data response:', error);
      this.dataRows = [];
      this.displayedColumns = [];
    }
  }

  private parseSchema(): any {
    if (this.parsedSchema) return this.parsedSchema;
    
    if (this.dataSource?.schemaJson) {
      try {
        this.parsedSchema = JSON.parse(this.dataSource.schemaJson);
        return this.parsedSchema;
      } catch (error) {
        console.error('Error parsing schema JSON:', error);
        return null;
      }
    }
    return null;
  }

  get isUploadedFile(): boolean {
    return this.uploadedFile !== null && !this.dataSource;
  }

  get displayName(): string {
    if (this.dataSource) {
      return this.dataSource.dataSourceName || 'Unnamed Data Source';
    }
    // Handle both {name, data} and {fileName, data} structures
    return this.uploadedFile?.name || this.uploadedFile?.data?.fileName || 'Unknown File';
  }

  get dataSourceType(): string {
    if (this.dataSource) {
      switch (this.dataSource.dataSourceTypeId) {
        case 1: return 'API Listener';
        case 2: return 'Database';
        case 3: return 'Uploaded File';
        default: return 'Unknown';
      }
    }
    return 'Uploaded File';
  }

  get dataSourceIcon(): string {
    if (this.dataSource) {
      switch (this.dataSource.dataSourceTypeId) {
        case 1: return 'api';
        case 2: return 'storage';
        case 3: return 'description';
        default: return 'source';
      }
    }
    return 'description';
  }

  get createdDate(): Date | null {
    if (this.dataSource?.createdAt) {
      return new Date(this.dataSource.createdAt);
    }
    if (this.uploadedFile?.data?.uploadedAt) {
      return new Date(this.uploadedFile.data.uploadedAt);
    }
    return null;
  }

  get updatedDate(): Date | null {
    if (this.dataSource?.updatedAt) {
      return new Date(this.dataSource.updatedAt);
    }
    return null;
  }

  get rowCount(): number | null {
    // Try uploaded file data first
    if (this.uploadedFile?.data?.rowCount) {
      return this.uploadedFile.data.rowCount;
    }
    
    // Check if data is an array and count rows
    const fileData = this.uploadedFile?.data?.data || this.uploadedFile?.data;
    if (Array.isArray(fileData)) {
      return fileData.length;
    }
    
    // Try schema for datasource
    const schema = this.parseSchema();
    if (schema) {
      // Handle new schema format: stats.n_rows from first column
      if (Array.isArray(schema) && schema.length > 0 && schema[0].stats?.n_rows) {
        return schema[0].stats.n_rows;
      }
      // Handle old format
      return schema.rowCount || schema.RowCount || null;
    }
    
    return null;
  }

  get columnCount(): number | null {
    // Try uploaded file data first
    if (this.uploadedFile?.data?.columns?.length) {
      return this.uploadedFile.data.columns.length;
    }
    
    // Check if data is an array and extract columns from first row
    const fileData = this.uploadedFile?.data?.data || this.uploadedFile?.data;
    if (Array.isArray(fileData) && fileData.length > 0 && typeof fileData[0] === 'object') {
      return Object.keys(fileData[0]).length;
    }
    
    // Try schema for datasource
    const schema = this.parseSchema();
    if (schema?.columns?.length) {
      return schema.columns.length;
    }
    if (schema?.Columns?.length) {
      return schema.Columns.length;
    }
    
    return null;
  }

  get columns(): Array<{name: string, type?: string}> {
    // Prioritize columnsWithTypes input if provided
    if (this.columnsWithTypes && this.columnsWithTypes.length > 0) {
      return this.columnsWithTypes;
    }
    
    // Try uploaded file data first
    if (this.uploadedFile?.data?.columns) {
      const cols = this.uploadedFile.data.columns;
      // Check if columns are already objects with name/type
      if (cols.length > 0 && typeof cols[0] === 'object' && cols[0].name) {
        return cols;
      }
      // Otherwise, convert string array to objects
      return cols.map((col: string) => ({ name: col }));
    }
    
    // Extract columns from data array
    const fileData = this.uploadedFile?.data?.data || this.uploadedFile?.data;
    if (Array.isArray(fileData) && fileData.length > 0 && typeof fileData[0] === 'object') {
      return Object.keys(fileData[0]).map(key => ({ name: key }));
    }
    
    // Try schema for datasource
    const schema = this.parseSchema();
    if (schema) {
      // Handle new schema format: [{column, atomic_dtype, soft_dtype, stats}, ...]
      if (Array.isArray(schema) && schema.length > 0 && schema[0].column) {
        return schema.map((col: any) => ({
          name: col.column,
          type: col.atomic_dtype
        }));
      }
      // Handle old format with columns property
      if (schema.columns?.length) {
        return schema.columns;
      }
      if (schema.Columns?.length) {
        return schema.Columns;
      }
    }
    
    return [];
  }

  get tags(): string[] {
    if (this.dataSource?.tags) {
      return this.dataSource.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
    }
    return [];
  }

  formatDate(date: Date | null): string {
    if (!date) return 'N/A';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  }

  onUpdateFileClick(): void {
    // Trigger the hidden file input
    const fileInput = document.getElementById('updateFileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.fileUpdate.emit(file);
      // Reset the input so the same file can be selected again if needed
      input.value = '';
    }
  }

  getCellValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value);
  }

  shouldShowTooltip(value: any): boolean {
    const stringValue = this.getCellValue(value);
    // Always show tooltip for non-empty values to ensure availability
    return stringValue.length > 0;
  }

  get isApiListener(): boolean {
    return this.dataSource?.dataSourceTypeId === 1;
  }

  get hasSchema(): boolean {
    return !!this.dataSource?.schemaJson && this.dataSource.schemaJson.trim() !== '';
  }

  get schemaColumns(): Array<{columnName: string, dataType: string}> {
    if (!this.hasSchema) return [];
    try {
      const schema = JSON.parse(this.dataSource!.schemaJson);
      if (Array.isArray(schema)) {
        return schema.map(col => ({
          // New format: {column, atomic_dtype, soft_dtype, stats}
          // Old format: {ColumnName, DataType}
          columnName: col.column || col.ColumnName || col.columnName || '',
          dataType: col.atomic_dtype || col.DataType || col.dataType || 'Unknown'
        }));
      }
      return [];
    } catch {
      return [];
    }
  }
}
